import { handleSetMusicalGenres } from '@/storage/modules/musicalGenres/reducer'
import { useEffect, useRef, useState } from 'react'
import { IoReload, IoTrash } from 'react-icons/io5'
import { useDispatch } from 'react-redux'
import colors from 'tailwindcss/colors'
import { FormMusicalGenres } from '../FormMusicalGenres'
import { useModal } from '@/hooks/useModal'
import { useToast } from '@/hooks/useToast'
import { api } from '@/services/api'
import { MusicalGenresDataProps } from '@/types/musicalGenresProps'
import { SceneDataProps } from '@/types/sceneProps'
import {
  EMPTY_PAGINATION_META,
  type PaginatedResponseProps,
  type PaginationMetaProps,
  normalizePaginationMeta,
} from '@/types/paginationProps'
import { EmptyState } from '../EmptyState'
import { MUSICAL_GENRES_UPDATED_EVENT } from '@/services/musicalGenresEvents'
import { SCENES_UPDATED_EVENT } from '@/services/scenesEvents'
import { fetchScenesOptions } from '@/services/scenes'

export function MusicalGenres() {
  const { openModal } = useModal()
  const { showToast } = useToast()
  const dispatch = useDispatch()
  const [sceneOptions, setSceneOptions] = useState<SceneDataProps[]>([])
  const [selectedSceneSlug, setSelectedSceneSlug] = useState('')
  const [genresList, setGenresList] = useState<MusicalGenresDataProps[]>([])
  const [paginationMeta, setPaginationMeta] = useState<PaginationMetaProps>(
    EMPTY_PAGINATION_META,
  )
  const [isLoadingInitial, setIsLoadingInitial] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isLoadingScenes, setIsLoadingScenes] = useState(false)

  const isLoadingMoreRef = useRef(false)
  const paginationMetaRef = useRef<PaginationMetaProps>(EMPTY_PAGINATION_META)
  const selectedSceneSlugRef = useRef('')
  const canLoadMoreRef = useRef(false)

  const canLoadMore =
    paginationMeta.lastPage > 0 &&
    paginationMeta.currentPage < paginationMeta.lastPage

  const handleFetchGenresPage = async (page: number, scene?: string) => {
    const response = await api.get<
      PaginatedResponseProps<MusicalGenresDataProps>
    >('/genres', {
      params: {
        page,
        ...(scene ? { scene } : {}),
      },
    })

    const pagination = normalizePaginationMeta(
      response.data.meta,
      response.data.data.length,
    )

    return {
      genres: response.data.data,
      pagination,
    }
  }

  const syncGenresStore = async () => {
    const responseGenres = await api
      .get('/genres', {
        params: {
          page: 1,
        },
      })
      .then((res) => res.data.data as MusicalGenresDataProps[])

    dispatch(handleSetMusicalGenres({ musicalGenres: responseGenres }))
  }

  const handleLoadFirstPage = async (scene?: string) => {
    try {
      setIsLoadingInitial(true)

      const response = await handleFetchGenresPage(1, scene)
      setGenresList(response.genres)
      setPaginationMeta(response.pagination)
      if (!scene) {
        await syncGenresStore()
      }
    } catch (error) {
      showToast('Error fetching musical genres', {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsLoadingInitial(false)
    }
  }

  const handleRefreshGenres = async () => {
    try {
      setIsRefreshing(true)
      const response = await handleFetchGenresPage(
        1,
        selectedSceneSlugRef.current || undefined,
      )
      setGenresList(response.genres)
      setPaginationMeta(response.pagination)

      if (!selectedSceneSlugRef.current) {
        await syncGenresStore()
      }
    } catch (error) {
      showToast('Error refreshing musical genres', {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleRemoveGenre = async (genreId: string) => {
    try {
      await api.delete(`/genres/${genreId}`)
      await syncGenresStore()
      setGenresList((prev) => prev.filter((genre) => genre.id !== genreId))
      setPaginationMeta((prev) => ({
        ...prev,
        totalItems: Math.max(0, prev.totalItems - 1),
      }))

      showToast('Musical genre removed successfully', {
        type: 'success',
        theme: 'light',
      })
    } catch (error) {
      showToast('Error removing musical genre', {
        type: 'error',
        theme: 'light',
      })
    }
  }

  const handleSelectScene = async (sceneSlug: string) => {
    setSelectedSceneSlug(sceneSlug)

    try {
      const response = await handleFetchGenresPage(1, sceneSlug || undefined)
      setGenresList(response.genres)
      setPaginationMeta(response.pagination)
    } catch (error) {
      showToast('Error filtering musical genres by scene', {
        type: 'error',
        theme: 'light',
      })
    }
  }

  useEffect(() => {
    handleLoadFirstPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    paginationMetaRef.current = paginationMeta
    selectedSceneSlugRef.current = selectedSceneSlug
    canLoadMoreRef.current = canLoadMore
  }, [canLoadMore, paginationMeta, selectedSceneSlug])

  useEffect(() => {
    let isMounted = true

    const loadScenes = async () => {
      try {
        setIsLoadingScenes(true)
        const scenes = await fetchScenesOptions()

        if (!isMounted) return

        setSceneOptions(scenes)
      } catch (error) {
        if (!isMounted) return

        showToast('Error fetching scenes', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        if (isMounted) {
          setIsLoadingScenes(false)
        }
      }
    }

    loadScenes()

    return () => {
      isMounted = false
    }
  }, [showToast])

  useEffect(() => {
    const loadMoreGenres = async () => {
      const nextPage = paginationMetaRef.current.currentPage + 1

      if (
        !canLoadMoreRef.current ||
        nextPage <= 0 ||
        isLoadingMoreRef.current
      ) {
        return
      }

      try {
        isLoadingMoreRef.current = true
        setIsLoadingMore(true)

        const response = await handleFetchGenresPage(
          nextPage,
          selectedSceneSlugRef.current || undefined,
        )
        paginationMetaRef.current = response.pagination
        canLoadMoreRef.current =
          response.pagination.lastPage > 0 &&
          response.pagination.currentPage < response.pagination.lastPage

        setGenresList((prev) => [...prev, ...response.genres])
        setPaginationMeta(response.pagination)
      } catch (error) {
        showToast('Error fetching more musical genres', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        isLoadingMoreRef.current = false
        setIsLoadingMore(false)
      }
    }

    const handleScroll = () => {
      if (!canLoadMoreRef.current || isLoadingMoreRef.current) {
        return
      }

      const viewportHeight = window.innerHeight
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const fullHeight = document.documentElement.scrollHeight

      if (!fullHeight) {
        return
      }

      const scrollProgress = (scrollTop + viewportHeight) / fullHeight

      if (scrollProgress >= 0.8) {
        loadMoreGenres()
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [showToast])

  useEffect(() => {
    const handleGenresUpdated = () => {
      handleLoadFirstPage(selectedSceneSlugRef.current || undefined)
    }

    window.addEventListener(MUSICAL_GENRES_UPDATED_EVENT, handleGenresUpdated)

    return () => {
      window.removeEventListener(
        MUSICAL_GENRES_UPDATED_EVENT,
        handleGenresUpdated,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleScenesUpdated = async () => {
      try {
        const scenes = await fetchScenesOptions()
        setSceneOptions(scenes)

        const currentSceneStillExists = scenes.some(
          (scene) => scene.slug === selectedSceneSlugRef.current,
        )

        if (!currentSceneStillExists && selectedSceneSlugRef.current) {
          setSelectedSceneSlug('')
          await handleLoadFirstPage()
        }
      } catch (error) {
        showToast('Error refreshing scenes', {
          type: 'error',
          theme: 'light',
        })
      }
    }

    window.addEventListener(SCENES_UPDATED_EVENT, handleScenesUpdated)

    return () => {
      window.removeEventListener(SCENES_UPDATED_EVENT, handleScenesUpdated)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <div className="mt-8">
        <h4 className="font-bold text-sm my-3">Scenes</h4>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => handleSelectScene('')}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
              !selectedSceneSlug
                ? 'border-purple-600 bg-purple-600 text-white'
                : 'border-gray-300 bg-white text-gray-600 hover:border-purple-300 hover:text-purple-600'
            }`}
          >
            All scenes
          </button>

          {sceneOptions.map((scene) => (
            <button
              key={scene.id}
              type="button"
              onClick={() => handleSelectScene(scene.slug)}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                selectedSceneSlug === scene.slug
                  ? 'border-purple-600 bg-purple-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-purple-300 hover:text-purple-600'
              }`}
            >
              {scene.title}
            </button>
          ))}

          <button
            type="button"
            title="Reload genres"
            onClick={handleRefreshGenres}
            disabled={isLoadingInitial || isRefreshing}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded border border-gray-300 bg-white text-purple-600 transition hover:border-purple-300 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <IoReload
              size={18}
              className={isRefreshing ? 'animate-spin' : undefined}
            />
          </button>

          {isLoadingScenes && (
            <span className="px-1 py-1.5 text-sm text-gray-500">
              Loading scenes...
            </span>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3 md:flex-col md:items-start">
        <p className="text-sm text-gray-500">
          Showing {genresList.length} of {paginationMeta.totalItems} genres
        </p>
      </div>

      {genresList.length === 0 && !isLoadingInitial && (
        <EmptyState
          title="No genres yet"
          description="Create the first musical genre to organize artists and musics."
        />
      )}

      {genresList.map((genre) => (
        <div key={genre.title} className="relative mt-8">
          <button
            title="Remove"
            className="absolute -right-1.5 -top-1.5 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-red-600 bg-red-600 shadow-sm transition hover:bg-red-700"
            onClick={() => {
              openModal({
                textConfirm: 'Delete',
                description: 'Do you really want to delete the musical genre?',
                title: 'Attention',
                confirm() {
                  handleRemoveGenre(genre.id)
                },
              })
            }}
          >
            <IoTrash size={14} color={colors.white} />
          </button>

          <button
            className="bg-white rounded-2xl p-7 top-5 flex items-center border hover:border-gray-300 w-full"
            onClick={() => {
              openModal({
                small: true,
                children: <FormMusicalGenres isExist={genre} />,
              })
            }}
          >
            <p className="font-semibold text-gray-500">{genre.title}</p>
          </button>
        </div>
      ))}

      {(isLoadingInitial || isLoadingMore) && (
        <div className="mt-8 flex items-center justify-center gap-3 text-sm text-gray-500">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />
          <p>
            {isLoadingInitial
              ? 'Loading musical genres...'
              : 'Loading more musical genres...'}
          </p>
        </div>
      )}
    </div>
  )
}
